/**
 * LIST MANAGER SERVICE
 *
 * Comprehensive contact management system with:
 * - Contact creation and management
 * - Custom list management
 * - Email template system
 * - Bulk email sending capabilities
 * - Segmentation and filtering
 *
 * SECURITY: All DB access goes through server-side API - NO getServiceRoleClient() in browser
 */

import { resendEmailService } from '../resendEmailService';
import { getSessionHeaders } from '../serverSession';

// =============================================================================
// API HELPER - All DB access goes through the server-side API
// =============================================================================
async function callListManagerAPI(operation: string, data: Record<string, any> = {}): Promise<any> {
 const response = await fetch('/api/admin/list-manager', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ operation, data })
 });
 const result = await response.json();
 if (!response.ok) {
 throw new Error(result.error || `List Manager API error: ${response.status}`);
 }
 return result.data !== undefined ? result.data : result;
}

export interface Contact {
 id: string;
 email: string;
 first_name?: string;
 last_name?: string;
 full_name?: string;
 phone?: string;
 company?: string;
 tags: string[];
 custom_fields: Record<string, any>;
 source: 'manual' | 'import' | 'website' | 'api';
 status: 'active' | 'unsubscribed' | 'bounced' | 'invalid';
 created_at: string;
 updated_at: string;
}

export interface ContactList {
 id: string;
 name: string;
 description?: string;
 contact_count: number;
 tags: string[];
 created_by: number;
 is_active: boolean;
 created_at: string;
 updated_at: string;
}

export interface EmailTemplate {
 id: string;
 name: string;
 subject: string;
 html_content: string;
 text_content?: string;
 template_type: 'newsletter' | 'announcement' | 'promotional' | 'transactional';
 variables: string[];
 created_by: number;
 is_active: boolean;
 created_at: string;
 updated_at: string;
}

export interface BulkEmailCampaign {
 id: string;
 name: string;
 template_id: string;
 list_ids: string[];
 subject: string;
 status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed';
 scheduled_at?: string;
 sent_at?: string;
 total_recipients: number;
 sent_count: number;
 failed_count: number;
 created_by: number;
 created_at: string;
 updated_at: string;
}

export class ListManagerService {
 private static instance: ListManagerService;

 private constructor() {}

 public static getInstance(): ListManagerService {
 if (!ListManagerService.instance) {
 ListManagerService.instance = new ListManagerService();
 }
 return ListManagerService.instance;
 }

 /**
 * CONTACT MANAGEMENT
 */

 async createContact(contactData: Omit<Contact, 'id' | 'created_at' | 'updated_at'>): Promise<Contact | null> {
 try {
 return await callListManagerAPI('create_contact', { contactData });
 } catch (error) {
 console.error('Error creating contact:', error);
 return null;
 }
 }

 async updateContact(contactId: string, updates: Partial<Contact>): Promise<Contact | null> {
 try {
 return await callListManagerAPI('update_contact', { contactId, updates });
 } catch (error) {
 console.error('Error updating contact:', error);
 return null;
 }
 }

 async deleteContact(contactId: string): Promise<boolean> {
 try {
 await callListManagerAPI('delete_contact', { contactId });
 return true;
 } catch (error) {
 console.error('Error deleting contact:', error);
 return false;
 }
 }

 async getContacts(filters?: {
 status?: string;
 tags?: string[];
 search?: string;
 limit?: number;
 offset?: number;
 }): Promise<{ contacts: Contact[]; total: number }> {
 try {
 return await callListManagerAPI('get_contacts', { filters });
 } catch (error) {
 console.error('Error fetching contacts:', error);
 return { contacts: [], total: 0 };
 }
 }

 /**
 * LIST MANAGEMENT
 */

 async createList(listData: Omit<ContactList, 'id' | 'contact_count' | 'created_at' | 'updated_at'>): Promise<ContactList | null> {
 try {
 return await callListManagerAPI('create_list', { listData });
 } catch (error) {
 console.error('Error creating list:', error);
 return null;
 }
 }

 async addContactsToList(listId: string, contactIds: string[]): Promise<boolean> {
 try {
 await callListManagerAPI('add_contacts_to_list', { listId, contactIds });
 return true;
 } catch (error) {
 console.error('Error adding contacts to list:', error);
 return false;
 }
 }

 async removeContactsFromList(listId: string, contactIds: string[]): Promise<boolean> {
 try {
 await callListManagerAPI('remove_contacts_from_list', { listId, contactIds });
 return true;
 } catch (error) {
 console.error('Error removing contacts from list:', error);
 return false;
 }
 }

 async getLists(createdBy?: number): Promise<ContactList[]> {
 try {
 return await callListManagerAPI('get_lists', { createdBy });
 } catch (error) {
 console.error('Error fetching lists:', error);
 return [];
 }
 }

 /**
 * EMAIL TEMPLATE MANAGEMENT
 */

 async createTemplate(templateData: Omit<EmailTemplate, 'id' | 'created_at' | 'updated_at'>): Promise<EmailTemplate | null> {
 try {
 return await callListManagerAPI('create_template', { templateData });
 } catch (error) {
 console.error('Error creating template:', error);
 return null;
 }
 }

 async getTemplates(createdBy?: number): Promise<EmailTemplate[]> {
 try {
 return await callListManagerAPI('get_templates', { createdBy });
 } catch (error) {
 console.error('Error fetching templates:', error);
 return [];
 }
 }

 /**
 * BULK EMAIL CAMPAIGNS
 */

 async createCampaign(campaignData: Omit<BulkEmailCampaign, 'id' | 'sent_count' | 'failed_count' | 'created_at' | 'updated_at'>): Promise<BulkEmailCampaign | null> {
 try {
 return await callListManagerAPI('create_campaign', { campaignData });
 } catch (error) {
 console.error('Error creating campaign:', error);
 return null;
 }
 }

 async sendCampaign(campaignId: string): Promise<{ success: boolean; error?: string }> {
 try {
 // Get campaign data and recipients from server
 const campaignData = await callListManagerAPI('send_campaign', { campaignId });

 // Send bulk email using ResendEmailService (client-side, uses Resend API key)
 const bulkEmailData = {
 recipients: campaignData.recipients,
 subject: campaignData.subject,
 htmlContent: campaignData.htmlContent,
 textContent: campaignData.textContent
 };

 const results = await resendEmailService.sendBulkEmail(bulkEmailData);

 // Count successful and failed sends
 const successCount = results.filter((r: any) => r.success).length;
 const failedCount = results.filter((r: any) => !r.success).length;

 // Update campaign results on server
 await callListManagerAPI('update_campaign_results', {
 campaignId, successCount, failedCount, results
 });

 return { success: true };

 } catch (error) {
 console.error('Error sending campaign:', error);

 // Mark campaign as failed on server
 try {
 await callListManagerAPI('update_campaign_failed', { campaignId });
 } catch (e) {
 console.error('Error marking campaign as failed:', e);
 }

 return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
 }
 }

 async getCampaigns(createdBy?: number): Promise<BulkEmailCampaign[]> {
 try {
 return await callListManagerAPI('get_campaigns', { createdBy });
 } catch (error) {
 console.error('Error fetching campaigns:', error);
 return [];
 }
 }

 /**
 * IMPORT/EXPORT FUNCTIONALITY
 */

 async importContacts(contacts: Omit<Contact, 'id' | 'created_at' | 'updated_at'>[]): Promise<{ success: number; failed: number }> {
 let successCount = 0;
 let failedCount = 0;

 for (const contactData of contacts) {
 const result = await this.createContact(contactData);
 if (result) {
 successCount++;
 } else {
 failedCount++;
 }
 }

 return { success: successCount, failed: failedCount };
 }

 async exportContacts(listId?: string): Promise<Contact[]> {
 try {
 return await callListManagerAPI('export_contacts', { listId });
 } catch (error) {
 console.error('Error exporting contacts:', error);
 return [];
 }
 }
}
